/**
 * Hybrid logical clock (Kulkarni et al., 2014), spec 9.1.
 *
 * Every change written on a device or on the server carries an HLC stamp. The stamp
 * orders events the way the wall clock would when clocks agree, and still gives a
 * total order that respects causality when device clocks drift (field laptops and
 * phones are often offline for days). The same code runs in the SQLite worker and
 * in the API, so it is platform-neutral: no DOM, no Node built-ins.
 *
 * Encoded form: `<wall ms, 13 digits>-<counter, 4 hex>-<node>`, which sorts
 * lexicographically in the same order as `hlcCompare`. The node is the 8-character device
 * node derived by `hlcNodeOf` from the device id, or `server` for system ops (9.8). Two
 * devices can never tie: wall and counter may coincide, the node never does.
 */

export interface Hlc {
  /** Physical component: milliseconds since the Unix epoch. */
  readonly wall: number
  /** Logical component: orders events that share the same `wall`. */
  readonly counter: number
  /** Stable id of the device or server that issued the stamp; breaks the last tie. */
  readonly node: string
}

export const HLC_MAX_COUNTER = 0xffff
/** A remote stamp more than this far ahead of the local clock is rejected by `hlcReceive`. */
export const HLC_MAX_DRIFT_MS = 5 * 60 * 1000
/** The server rejects an op whose wall clock is further ahead than this (spec 9.5 step 5). */
export const HLC_SERVER_MAX_AHEAD_MS = 24 * 60 * 60 * 1000
/** Length of a device node inside a stamp (spec 9.1: "device id, 8 chars"). */
export const HLC_NODE_LENGTH = 8
/** The node of system ops emitted by the server (spec 9.8). */
export const HLC_SERVER_NODE = 'server'

/** 13 digits cover every millisecond up to the year 2286. */
const WALL_DIGITS = 13
const COUNTER_DIGITS = 4
const NODE_PATTERN = /^[A-Za-z0-9_]{1,16}$/
const ENCODED_PATTERN = /^(\d{13})-([0-9a-f]{4})-([A-Za-z0-9_]{1,16})$/

export type HlcErrorReason = 'counter-overflow' | 'clock-drift' | 'invalid-node' | 'invalid-stamp'

export class HlcError extends Error {
  readonly reason: HlcErrorReason

  constructor(reason: HlcErrorReason, message: string) {
    super(message)
    this.name = 'HlcError'
    this.reason = reason
  }
}

function assertNode(node: string): void {
  if (!NODE_PATTERN.test(node)) {
    throw new HlcError('invalid-node', `HLC node id must match ${NODE_PATTERN.source}: "${node}"`)
  }
}

function assertWall(wall: number): void {
  if (!Number.isSafeInteger(wall) || wall < 0 || String(wall).length > WALL_DIGITS) {
    throw new HlcError('invalid-stamp', `HLC wall time out of range: ${wall}`)
  }
}

function withCounter(wall: number, counter: number, node: string): Hlc {
  if (counter > HLC_MAX_COUNTER) {
    throw new HlcError('counter-overflow', `HLC counter overflow at wall ${wall}`)
  }
  return { wall, counter, node }
}

/**
 * The node a device stamps with: the last 8 characters of its id once anything outside
 * `[A-Za-z0-9_]` is dropped. For a UUID v7 device id those are random bits (the leading
 * characters are the enrolment time, shared by devices enrolled in the same minute).
 * `server` is kept as is.
 */
export function hlcNodeOf(deviceId: string): string {
  if (deviceId === HLC_SERVER_NODE) return deviceId
  const clean = deviceId.replace(/[^A-Za-z0-9_]/g, '')
  if (clean.length === 0) {
    throw new HlcError('invalid-node', `Cannot derive an HLC node from device id "${deviceId}"`)
  }
  return clean.slice(-HLC_NODE_LENGTH)
}

/** The zero clock for a node: every real stamp from that node sorts after it. */
export function hlcZero(node: string): Hlc {
  assertNode(node)
  return { wall: 0, counter: 0, node }
}

/** Stamp a local event (a write on this node). `now` is the local wall clock in ms. */
export function hlcTick(local: Hlc, now: number): Hlc {
  assertWall(now)
  const wall = Math.max(local.wall, now)
  const counter = wall === local.wall ? local.counter + 1 : 0
  return withCounter(wall, counter, local.node)
}

/**
 * Merge a stamp received from another node (a pulled change) into the local clock.
 * The result is strictly greater than both `local` and `remote`. The sync loop passes
 * `HLC_SERVER_MAX_AHEAD_MS` so every op the server accepted is receivable.
 */
export function hlcReceive(
  local: Hlc,
  remote: Hlc,
  now: number,
  maxDriftMs: number = HLC_MAX_DRIFT_MS,
): Hlc {
  assertWall(now)
  assertWall(remote.wall)
  if (remote.wall - now > maxDriftMs) {
    throw new HlcError(
      'clock-drift',
      `Remote HLC is ${remote.wall - now} ms ahead of the local clock (limit ${maxDriftMs} ms)`,
    )
  }
  const wall = Math.max(local.wall, remote.wall, now)
  let counter: number
  if (wall === local.wall && wall === remote.wall) {
    counter = Math.max(local.counter, remote.counter) + 1
  } else if (wall === local.wall) {
    counter = local.counter + 1
  } else if (wall === remote.wall) {
    counter = remote.counter + 1
  } else {
    counter = 0
  }
  return withCounter(wall, counter, local.node)
}

/** Total order: wall, then counter, then node id. */
export function hlcCompare(a: Hlc, b: Hlc): -1 | 0 | 1 {
  if (a.wall !== b.wall) return a.wall < b.wall ? -1 : 1
  if (a.counter !== b.counter) return a.counter < b.counter ? -1 : 1
  if (a.node !== b.node) return a.node < b.node ? -1 : 1
  return 0
}

/** The same order on encoded stamps; plain string comparison is enough for valid stamps. */
export function hlcCompareEncoded(a: string, b: string): -1 | 0 | 1 {
  return a === b ? 0 : a < b ? -1 : 1
}

export function hlcEncode(h: Hlc): string {
  assertWall(h.wall)
  assertNode(h.node)
  if (!Number.isInteger(h.counter) || h.counter < 0 || h.counter > HLC_MAX_COUNTER) {
    throw new HlcError('invalid-stamp', `HLC counter out of range: ${h.counter}`)
  }
  const wall = String(h.wall).padStart(WALL_DIGITS, '0')
  const counter = h.counter.toString(16).padStart(COUNTER_DIGITS, '0')
  return `${wall}-${counter}-${h.node}`
}

export function hlcDecode(encoded: string): Hlc {
  const match = ENCODED_PATTERN.exec(encoded)
  if (match === null) {
    throw new HlcError('invalid-stamp', `Not an encoded HLC: "${encoded}"`)
  }
  const [, wall = '', counter = '', node = ''] = match
  return { wall: Number(wall), counter: Number.parseInt(counter, 16), node }
}

export function isHlc(value: unknown): value is string {
  return typeof value === 'string' && ENCODED_PATTERN.test(value)
}
